/**
 * ProgressListCard — shared types, Zod prop schema and tokens.
 *
 * A card listing one to four progress rows, closed by a footer band that says
 * what comes next. Each row opens with its label and count on one line
 * ("Chat • 1/3 done"), then its indicator — a row of segments, one per unit of
 * the total, or a row of large step circles — then an optional eyebrow +
 * title, and either a plain body sentence or an emphasised callout panel that
 * can carry a dismiss control. The footer band runs flush to the card's edges
 * under a hairline rule: a status glyph (a lock, a calendar, or a filled tick
 * once something is ready), a title, a sentence, and the card's only call to
 * action when there is one. The header is optional; the artboards leave the
 * clock to the surface around the card.
 *
 * Copy-pass-through (ADR-017 Rule 17): every string is supplied by the host;
 * the component never carries demo copy. Shape/role naming per ADR-020 Rule 4:
 * `segments` / `dots` describe indicator shape, `callout` a panel role, and the
 * footer's `status` names what the band announces, which fixes both its glyph
 * and its surface — one closed set rather than an icon and a tone that could
 * be paired into bands the design never draws.
 *
 * Source of truth: the FINAL artboards (Home — week 1 first open, week 1 first
 * plan unlocked, week 2 midweek, week 2 Sunday) in the "Weekly Plan Card"
 * design canvas.
 */

import { z } from 'zod';
import { actionIdInputSchema } from '@galapagos/product-analytics';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';
import { CardRadiusEnum, type CardBackground } from '../Card/Card';
import type { IconName } from '../../elements/icons/registry';
import type { TextVariant } from '../Text/Text';

/** Defensive authoring bound: the card is a summary, not a list surface. */
export const PROGRESS_LIST_CARD_MAX_ROWS = 4;

export const ProgressListCardIndicatorKind = z.enum(['segments', 'dots']);
export type ProgressListCardIndicatorKind = z.infer<typeof ProgressListCardIndicatorKind>;

export const progressListCardIndicatorSchema = z
  .object({
    kind: ProgressListCardIndicatorKind,
    /** Completed count; values above `total` are clamped at render. */
    value: z.number().int().min(0),
    /** The indicator's full extent: one segment or circle per unit. */
    total: z.number().int().positive(),
  })
  .strict();
export type ProgressListCardIndicator = z.infer<typeof progressListCardIndicatorSchema>;

export const progressListCardCtaSchema = z
  .object({
    label: z.string().min(1),
    actionId: actionIdInputSchema,
    accessibilityHint: z.string().optional(),
    testID: z.string().optional(),
  })
  .strict();
export type ProgressListCardCta = z.infer<typeof progressListCardCtaSchema>;

/** A callout's dismiss control: a small close glyph in its top-right corner. */
export const progressListCardDismissSchema = z
  .object({
    actionId: actionIdInputSchema,
    accessibilityLabel: z.string().min(1),
    testID: z.string().optional(),
  })
  .strict();
export type ProgressListCardDismiss = z.infer<typeof progressListCardDismissSchema>;

/** An emphasised panel under a row's title, optionally dismissible. */
export const progressListCardCalloutSchema = z
  .object({
    title: z.string().optional(),
    body: z.string().min(1),
    dismiss: progressListCardDismissSchema.optional(),
  })
  .strict();
export type ProgressListCardCallout = z.infer<typeof progressListCardCalloutSchema>;

export const progressListCardRowSchema = z
  .object({
    /** The row's name, rendered bold before the count (e.g. "Chat"). */
    label: z.string().min(1),
    /** The row's progress, rendered after the label (e.g. "1/3 done", "4/7 days"). */
    count: z.string().min(1),
    /**
     * The row's progress picture. Optional: a row with nothing to count up
     * (a paused track, a control that is off) shows its label line and words
     * alone, and its accessible name carries what the picture would.
     */
    indicator: progressListCardIndicatorSchema.optional(),
    /** Rendered as one extra highlighted pill once a count has risen past `indicator.total` (e.g. "+1"). */
    overflowLabel: z.string().optional(),
    /** Small caption above the title (e.g. what the title names). */
    eyebrow: z.string().optional(),
    title: z.string().optional(),
    /** A plain sentence under the title. */
    body: z.string().optional(),
    /** An emphasised panel under the title. */
    callout: progressListCardCalloutSchema.optional(),
    /**
     * A row-level call to action, rendered as a text button after the row's
     * words and reported through the same `onCtaPress(actionId)` seam as the
     * footer's, so the host binds it under `on` exactly like the footer's.
     */
    cta: progressListCardCtaSchema.optional(),
    /** Announced for the whole row's text; the indicator itself is decorative. */
    accessibilityLabel: z.string().min(1),
    testID: z.string().optional(),
  })
  .strict();
export type ProgressListCardRow = z.infer<typeof progressListCardRowSchema>;

/**
 * What the footer band announces. `locked`: the thing is still being earned
 * (lock glyph, plain surface). `scheduled`: it arrives on a known day
 * (calendar glyph, plain surface). `ready`: it is here (filled tick, the
 * highlighted surface) — the state that normally carries the call to action.
 */
export const ProgressListCardFooterStatus = z.enum(['locked', 'scheduled', 'ready']);
export type ProgressListCardFooterStatus = z.infer<typeof ProgressListCardFooterStatus>;

export const progressListCardFooterSchema = z
  .object({
    status: ProgressListCardFooterStatus,
    title: z.string().min(1),
    body: z.string().min(1),
    /** The card's call to action, rendered at the band's trailing edge. */
    cta: progressListCardCtaSchema.optional(),
    testID: z.string().optional(),
  })
  .strict();
export type ProgressListCardFooter = z.infer<typeof progressListCardFooterSchema>;

/**
 * Optional density. `compact` tightens the card for a surface that stacks it
 * among other cards: a narrower body inset, a wider gap between rows, wider
 * spacing between step circles, a taller footer band with a larger glyph, and a callout
 * padded evenly with body-size copy. Absent keeps today's geometry; a build
 * that predates the option drops it and draws today's card.
 */
export const ProgressListCardDensity = z.enum(['compact']);
export type ProgressListCardDensity = z.infer<typeof ProgressListCardDensity>;

/**
 * Optional callout surface. `neutral` paints the callout on the primary
 * background with a smaller corner instead of the highlighted surface. Absent
 * keeps the highlighted callout.
 */
export const ProgressListCardCalloutTone = z.enum(['neutral']);
export type ProgressListCardCalloutTone = z.infer<typeof ProgressListCardCalloutTone>;

export const progressListCardPropsSchema = z.object({
  /** Optional bold header line. */
  title: z.string().optional(),
  /** Optional small header line beside or instead of the title. */
  meta: z.string().optional(),
  rows: z.array(progressListCardRowSchema).min(1).max(PROGRESS_LIST_CARD_MAX_ROWS),
  /** The closing band: what comes next, and the card's call to action. */
  footer: progressListCardFooterSchema.optional(),
  /** Optional corner radius token for the card shell; absent keeps the canonical card radius. */
  radius: CardRadiusEnum.optional(),
  /** Optional density; see `ProgressListCardDensity`. */
  density: ProgressListCardDensity.optional(),
  /** Optional callout surface; see `ProgressListCardCalloutTone`. */
  calloutTone: ProgressListCardCalloutTone.optional(),
  accessibilityLabel: z.string().min(1),
  testID: z.string().optional(),
});
export type ProgressListCardProps = z.infer<typeof progressListCardPropsSchema>;

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

/** The body's inset inside the card shell; the card owns its insets so the footer band can run to the edges. */
export const PROGRESS_LIST_CARD_BODY_PADDING = spacing.lg;
/** Vertical rhythm between the header and each row. */
export const PROGRESS_LIST_CARD_SECTION_GAP = spacing.lg;
/** Gap between the blocks inside one row (label line → indicator → title block → body/callout). */
export const PROGRESS_LIST_CARD_ROW_GAP = spacing.sm;
/** Gap between an eyebrow and its title, and between a callout's or the footer's title and body. */
export const PROGRESS_LIST_CARD_TEXT_GAP = spacing.xs;
/** Gap between the label, the separator and the count on the row's first line. */
export const PROGRESS_LIST_CARD_INLINE_GAP = spacing.xs;
/** Separator glyph between a row's label and its count — layout glue, not copy. */
export const PROGRESS_LIST_CARD_LINE_SEPARATOR = '•';

/** Segment indicator: pill-shaped segments, one per unit, filled left to right. */
export const PROGRESS_LIST_CARD_SEGMENT_HEIGHT = sizing.progressDot.large;
export const PROGRESS_LIST_CARD_SEGMENT_GAP = spacing.s;
export const PROGRESS_LIST_CARD_SEGMENT_RADIUS = radius.rounded;
export const PROGRESS_LIST_CARD_SEGMENT_FILLED_COLOR = colors.mossGreen['500'];
export const PROGRESS_LIST_CARD_SEGMENT_EMPTY_COLOR = colors.background.primary;
/** The overflow pill hugs its label on the pale moss surface, so "+1" reads as credit, not debt. */
export const PROGRESS_LIST_CARD_OVERFLOW_BACKGROUND = colors.mossGreen['300'];
export const PROGRESS_LIST_CARD_OVERFLOW_PADDING = spacing.s;
/** The overflow label renders with the `success` text colour, set on Text directly. */
export const PROGRESS_LIST_CARD_OVERFLOW_VARIANT: TextVariant = 'c2Regular';

/** Dots indicator: large step circles (the medium icon size), dark when complete. */
export const PROGRESS_LIST_CARD_DOT_SIZE = sizing.icon.md;

/** Callout panel under a row's title. */
export const PROGRESS_LIST_CARD_CALLOUT_BACKGROUND = colors.background.mistblue;
export const PROGRESS_LIST_CARD_CALLOUT_RADIUS = radius.xl;
export const PROGRESS_LIST_CARD_CALLOUT_PADDING_VERTICAL = spacing.sm;
export const PROGRESS_LIST_CARD_CALLOUT_PADDING_HORIZONTAL = spacing.m;
export const PROGRESS_LIST_CARD_CALLOUT_GAP = spacing.sm;
/** Footer and dismiss glyphs: the compact icon size, in the insight tint the artboards draw them in. */
export const PROGRESS_LIST_CARD_GLYPH_SIZE = sizing.icon.compact;
export const PROGRESS_LIST_CARD_GLYPH_COLOR = colors.text.insight;

/** The footer band: a hairline rule above, flush to the card's edges, its glyph aligned with the body's leading edge. */
export const PROGRESS_LIST_CARD_FOOTER_GAP = spacing.sm;
export const PROGRESS_LIST_CARD_FOOTER_PADDING_VERTICAL = spacing.m;
export const PROGRESS_LIST_CARD_FOOTER_PADDING_LEADING = spacing.lg;
export const PROGRESS_LIST_CARD_FOOTER_PADDING_TRAILING = spacing.m;
export const PROGRESS_LIST_CARD_FOOTER_RULE_COLOR = colors.stroke.divider;
export const PROGRESS_LIST_CARD_FOOTER_RULE_WIDTH = sizing.stroke.hairline;
/** The `ready` tick: a filled disc the glyph size, carrying a small inverted check. */
export const PROGRESS_LIST_CARD_FOOTER_TICK_BACKGROUND = colors.background.dark;
export const PROGRESS_LIST_CARD_FOOTER_TICK_COLOR = colors.icon.inverted;
export const PROGRESS_LIST_CARD_FOOTER_TICK_ICON_SIZE = sizing.icon.xxs;
/** Minimum tappable extent for the dismiss control (ADR-025 Rule 5). */
export const PROGRESS_LIST_CARD_MIN_TOUCH_TARGET = sizing.touchTarget.minimum;

// Compact density. Values that are not a single token are composed from tokens
// so they move with the scale rather than drifting as literals.

/** Compact body inset: the card sits in a column of cards, so it matches their 16pt inset. */
export const PROGRESS_LIST_CARD_COMPACT_BODY_PADDING = spacing.m;
/** Compact gap between rows: one step wider than the default so each row reads as its own block. */
export const PROGRESS_LIST_CARD_COMPACT_SECTION_GAP = spacing.lg + spacing.xs;
/** Compact footer band: taller vertical padding, and an even horizontal inset matching the body. */
export const PROGRESS_LIST_CARD_COMPACT_FOOTER_PADDING_VERTICAL = spacing.m + spacing.xs;
export const PROGRESS_LIST_CARD_COMPACT_FOOTER_PADDING_HORIZONTAL = spacing.m;
/** Compact footer glyph: the small icon size, larger than the default compact glyph. */
export const PROGRESS_LIST_CARD_COMPACT_FOOTER_GLYPH_SIZE = sizing.icon.sm;
/** Compact footer title → body gap. */
export const PROGRESS_LIST_CARD_COMPACT_FOOTER_TEXT_GAP = spacing.xxs;
/** Compact gap between step circles in a dots indicator. */
export const PROGRESS_LIST_CARD_COMPACT_DOT_GAP = spacing.sm;
/** Compact callout: even padding on all sides. */
export const PROGRESS_LIST_CARD_COMPACT_CALLOUT_PADDING = spacing.sm;
/** Compact callout body: body-size copy rather than the default small copy. */
export const PROGRESS_LIST_CARD_COMPACT_CALLOUT_BODY_VARIANT: TextVariant = 'b2Regular';

/** Neutral callout tone: the primary background, with the large (not extra-large) corner. */
export const PROGRESS_LIST_CARD_NEUTRAL_CALLOUT_BACKGROUND = colors.background.primary;
export const PROGRESS_LIST_CARD_NEUTRAL_CALLOUT_RADIUS = radius.lg;

export const PROGRESS_LIST_CARD_TITLE_VARIANT: TextVariant = 'b1Bold';
export const PROGRESS_LIST_CARD_META_VARIANT: TextVariant = 'b3Regular';
export const PROGRESS_LIST_CARD_LABEL_VARIANT: TextVariant = 'b1Bold';
export const PROGRESS_LIST_CARD_COUNT_VARIANT: TextVariant = 'b1Regular';
export const PROGRESS_LIST_CARD_EYEBROW_VARIANT: TextVariant = 'c1Medium';
export const PROGRESS_LIST_CARD_ROW_TITLE_VARIANT: TextVariant = 'b1Bold';
export const PROGRESS_LIST_CARD_BODY_VARIANT: TextVariant = 'b2Regular';
export const PROGRESS_LIST_CARD_CALLOUT_TITLE_VARIANT: TextVariant = 'b2Bold';
export const PROGRESS_LIST_CARD_CALLOUT_BODY_VARIANT: TextVariant = 'b3Regular';
export const PROGRESS_LIST_CARD_FOOTER_TITLE_VARIANT: TextVariant = 'b2Bold';
export const PROGRESS_LIST_CARD_FOOTER_BODY_VARIANT: TextVariant = 'b3Regular';

// ---------------------------------------------------------------------------
// Helpers shared by both platforms
// ---------------------------------------------------------------------------

/** Clamp an indicator's completed count into `[0, total]`; non-finite reads as empty. */
export function clampProgressListCardValue(indicator: ProgressListCardIndicator): number {
  if (!Number.isFinite(indicator.value)) return 0;
  return Math.min(indicator.total, Math.max(0, Math.floor(indicator.value)));
}

/** Whether segment `index` (0-based) of a segments indicator is filled. */
export function progressListCardSegmentFilled(
  indicator: ProgressListCardIndicator,
  index: number,
): boolean {
  return index < clampProgressListCardValue(indicator);
}

/** The band's surface: highlighted only once what it announces is ready. */
export function progressListCardFooterBand(status: ProgressListCardFooterStatus): CardBackground {
  return status === 'ready' ? 'mistblue' : 'container';
}

/** The outlined glyph a band shows while it is not yet ready; the ready band draws a filled tick instead. */
export function progressListCardFooterGlyph(
  status: ProgressListCardFooterStatus,
): Extract<IconName, 'lock' | 'calendar'> | null {
  if (status === 'locked') return 'lock';
  if (status === 'scheduled') return 'calendar';
  return null;
}

/** Strip a dismissed callout from its row so the host can hide it without re-authoring the rows. */
export function progressListCardWithoutDismissedCallouts(
  rows: readonly ProgressListCardRow[],
  dismissed: ReadonlySet<string>,
): ProgressListCardRow[] {
  return rows.map((row) => {
    if (!row.callout?.dismiss || !dismissed.has(row.callout.dismiss.actionId)) return row;
    const { callout: _callout, ...rest } = row;
    return rest;
  });
}
